import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import themeLight from "@lib/theme/light";
import Copyright from "../shared/Copyright";
import { Toaster } from "react-hot-toast";
import {
  alpha,
  Alert,
  Box,
  Button,
  FormControl,
  styled,
  Link,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import Logo from "../shared/Logo";
import { useEffect, useState } from "react";
import axios from "axios";
import Label from "@components/shared/form/Label";
import TextField from "@components/shared/form/TextField";
import LinkedInIcon from "@mui/icons-material/LinkedIn";

const Container = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "100vh",
});

const Content = styled(Box)({
  width: "100%",
  maxWidth: "600px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  rowGap: "34px",
});

const Form = styled(Box)(({ theme }) => ({
  width: "100%",
  padding: "40px",
  position: "relative",
  display: "flex",
  flexDirection: "column",
  backgroundColor: alpha(theme.palette.primary.light, 0.17),
  overflow: "hidden",
  borderRadius: "6px",
}));

const Fields = styled(Box)({
  display: "flex",
  flexDirection: "column",
  width: "100%",
});

const PinkPill = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "20px",
  right: "-50px",
  width: "180px",
  height: "83px",
  borderRadius: "9999px",
  backgroundColor: alpha(theme.palette.secondary.main, 0.9),
}));

const BluePill = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "70px",
  right: "-30px",
  width: "90px",
  height: "66px",
  borderRadius: "9999px",
  backgroundColor: alpha(theme.palette.primary.light, 0.7),
}));

const LoginField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: theme.palette.primary.main,
    },
    "&:hover fieldset": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
    },
  },

  "input[data-com-onepassword-filled='light']": {
    backgroundColor: "transparent !important",
  },
}));

const LinkedInButton = styled(Button)(({ theme }) => ({
  width: "100%",
  marginTop: "24px",
  marginBottom: "24px",
  backgroundColor: "#fff",
  color: "#0077B5",
  border: "2px solid #0077B5",
  "&:hover": {
    backgroundColor: "#f3f9ff",
    border: "2px solid #006097",
  },
  textTransform: "none",
  display: "flex",
  gap: "12px",
  padding: "12px 16px",
  fontSize: "16px",
  fontWeight: 600,
  borderRadius: "8px",
  "& .MuiSvgIcon-root": {
    fontSize: "24px",
  },
}));

const OrDivider = styled(Box)(({ theme }) => ({
  width: "100%",
  display: "flex",
  alignItems: "center",
  marginBottom: "24px",
  "& .MuiDivider-root": {
    flexGrow: 1,
  },
  "& .MuiTypography-root": {
    margin: "0 16px",
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
}));

const Mode = {
  LOGIN: "login",
  FORGOT_PASSWORD: "forgot-password",
  INACTIVE: "inactive",
};

export default function Login() {
  const [mode, setMode] = useState(Mode.LOGIN);
  const [inactiveMessage, setInactiveMessage] = useState("");
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);
  const msg = urlParams.get("msg");

  useEffect(() => {
    // Show cookie consent
    document.querySelector(".cookie-consent")?.classList.add("show");
  }, []);

  useEffect(() => {
    if (msg === "no-token") {
      setErrorMessage("Token already used or not found.");
    }
    if (msg === "inactive") {
      setErrorMessage("Your account is inactive.");
      setMode(Mode.INACTIVE);
    }
    if (msg === "trial_expired") {
      setErrorMessage("Your trial has expired.");
      setMode(Mode.INACTIVE);
    }
    if (msg === "not_registered") {
      setErrorMessage("You do not have a registered account.");
      setMode(Mode.INACTIVE);
    }
    // remove query string from URL
    window.history.replaceState({}, document.title, window.location.pathname);
  }, [msg]);

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
      },
    },
  });

  const handleLogin = () => {
    setIsLoading(true);

    const payload = {
      email: creds.email.trim(),
      password: creds.password,
      _token: document.querySelector("[name='_token']").value,
    };

    setErrorMessage("");
    axios
      .post("/login", payload)
      .then(() => {
        axios
          .post("/account-status")
          .then(() => {
            window.location = "/";
          })
          .catch((error) => {
            // Check if trial expired
            setMode(Mode.INACTIVE);
            setInactiveMessage(
              error.response.data?.message ?? "There's an issue with your account"
            );
          });
      })
      .catch((error) => {
        setIsLoading(false);
        setErrorMessage(error.response.data?.errors?.email ?? error.response.data.message);
      });
  };

  const handleContactSupport = () => {
    // Open email client with support email
    window.location.href = "mailto:support@floats.ai?subject=Trial expired";
  };

  const handleForgotPassword = () => {
    setIsLoading(true);
    const payload = {
      email: creds.email,
      _token: document.querySelector("[name='_token']").value,
    };

    setErrorMessage("");
    axios
      .post("/password/email", payload)
      .then((response) => {
        setIsLoading(false);
        setSuccessMessage("A password reset link has been sent to your email address.");
      })
      .catch((error) => {
        setIsLoading(false);
        setErrorMessage(error.response.data?.errors?.email ?? error.response.data.message);
      });
  };

  return (
    <ThemeProvider theme={themeLight}>
      <QueryClientProvider client={queryClient}>
        <Toaster position="top-center" />
        <Box sx={{ display: "flex" }}>
          <CssBaseline />
          <Container>
            <Content>
              <Box>
                <Logo />
              </Box>
              <Form>
                <PinkPill />
                <BluePill />
                {mode !== Mode.INACTIVE && (
                  <Fields>
                    {errorMessage && (
                      <Alert sx={{ mb: 2, opacity: 0.8 }} severity="error">
                        {errorMessage}
                      </Alert>
                    )}
                    {successMessage && (
                      <Alert sx={{ mb: 2, opacity: 0.8 }} severity="success">
                        {successMessage}
                      </Alert>
                    )}
                    {mode === Mode.LOGIN && (
                      <>
                        <LinkedInButton
                          variant="outlined"
                          onClick={() => {
                            // Redirect to LinkedIn login page
                            window.location.href = "/auth/linkedin-openid/login";
                          }}
                        >
                          <LinkedInIcon />
                          Sign in with LinkedIn
                        </LinkedInButton>

                        <OrDivider>
                          <Divider />
                          <Typography variant="body1">OR</Typography>
                          <Divider />
                        </OrDivider>

                        <FormControl sx={{ width: "100%", mb: 4 }}>
                          <Label>Email</Label>
                          <LoginField
                            fullWidth
                            id="email"
                            variant="outlined"
                            autoFocus
                            value={creds.email}
                            onChange={(event) =>
                              setCreds((creds) => ({ ...creds, email: event.target.value }))
                            }
                          />
                        </FormControl>

                        <FormControl>
                          <Label>Password</Label>
                          <LoginField
                            type="password"
                            fullWidth
                            variant="outlined"
                            value={creds.password}
                            onChange={(event) =>
                              setCreds((prev) => ({ ...prev, password: event.target.value }))
                            }
                          />
                          <Box width="100%" sx={{ textAlign: "right", mt: 1 }}>
                            <Link href="#" onClick={() => setMode(Mode.FORGOT_PASSWORD)}>
                              Forgot password?
                            </Link>
                          </Box>
                        </FormControl>

                        <Button
                          type="submit"
                          variant="contained"
                          fullWidth
                          disabled={isLoading}
                          onClick={handleLogin}
                          sx={{ mt: 3, mb: 2 }}
                        >
                          {isLoading ? <CircularProgress size={24} /> : "Sign In"}
                        </Button>
                      </>
                    )}
                    {mode === Mode.FORGOT_PASSWORD && (
                      <Box sx={{ mt: 3, position: "relative" }}>
                        <FormControl sx={{ width: "100%", mb: 4 }}>
                          <Label>Email</Label>
                          <LoginField
                            fullWidth
                            id="email"
                            variant="outlined"
                            autoFocus
                            value={creds.email}
                            onChange={(event) =>
                              setCreds((creds) => ({ ...creds, email: event.target.value }))
                            }
                          />
                        </FormControl>

                        <Button
                          sx={{ width: "100%" }}
                          variant="contained"
                          disabled={isLoading}
                          onClick={handleForgotPassword}
                        >
                          Send password reset link
                        </Button>
                        {isLoading && (
                          <CircularProgress
                            size={24}
                            sx={{
                              color: (theme) => theme.palette.primary.main,
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              marginTop: "-12px",
                              marginLeft: "-12px",
                            }}
                          />
                        )}
                      </Box>
                    )}
                  </Fields>
                )}
                {mode === Mode.INACTIVE && (
                  <Box>
                    {errorMessage && (
                      <Alert sx={{ mb: 2, opacity: 0.8 }} severity="error">
                        {errorMessage}
                      </Alert>
                    )}
                    <h1 class="text-primary-500">{inactiveMessage}</h1>
                    <p>Please contact support to continue using Floats.</p>
                    <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setMode(Mode.LOGIN);
                          setErrorMessage("");
                          setInactiveMessage("");
                        }}
                      >
                        Back to Login
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => {
                          handleContactSupport();
                        }}
                      >
                        Contact support
                      </Button>
                    </Box>
                  </Box>
                )}
              </Form>
            </Content>
          </Container>
        </Box>
        <Copyright sx={{ pt: 4 }} />
      </QueryClientProvider>
    </ThemeProvider>
  );
}
